import {useEffect, useRef, useState} from "react";

export const ImageCaptureState = Object.freeze({
    EXPLAIN: Symbol("EXPLAIN"),
    ACCESS_DENIED: Symbol("ACCESS_DENIED"),
    STREAMING: Symbol("STREAMING"),
    CAPTURED: Symbol("CAPTURED"),
    DONE: Symbol("DONE"),
});
export const ImageCaptureHelper = ({setter, close}) => {

    const [foto1, setFoto1] = useState(null);
    const [foto2, setFoto2] = useState(null);
    const [state, setState] = useState(ImageCaptureState.EXPLAIN)
    const [showLandscapeMsg, setShowLandscapeMsg] = useState(false);

    const keepRendering = useRef(false);
    const renderWireframe = useRef(true);
    const streamRef = useRef(null);

    const streamMap = useRef(new Map());

    useEffect(() => {
        document.body.classList.add("no-scroll");
        const mql = window.matchMedia("(orientation: landscape)");

        mql.addEventListener("change", orientationChanged);;

        orientationChanged(mql);

        return () => {
            document.body.scrollTo({top: 0, behavior: "instant"});
            document.body.classList.remove("no-scroll");
            mql.removeEventListener("change", orientationChanged);
        }

    }, []);

    useEffect(() => {
        if(state == ImageCaptureState.STREAMING){
            startVideoFeed();
        }
        if(state == ImageCaptureState.DONE){
            done();
        }
    }, [state]);

    const orientationChanged = (e) => {
        setShowLandscapeMsg(e.matches);
    }

    const initializeCameraStream = async () => {
        try {
            const constraints = {
                video: {width: { ideal: 1920 }, height: { ideal: 1080 }, facingMode: {ideal: "environment"}}
            }

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            setState(ImageCaptureState.STREAMING);
            return stream;
        } catch (err) {
            setState(ImageCaptureState.ACCESS_DENIED);
            console.log("No rear camera detected or access denied.", err);
        }
    }

    const startVideoFeed = () => {
        keepRendering.current = true;
        const canvas = getCanvas();
        const video = getVideo();
        if(!video.srcObject){
            video.srcObject = streamRef.current;
            try {
                const playPromise = video.play();
                if (playPromise?.catch) {
                    playPromise.catch((err) => handleVideoPlaybackError(video, err));
                }
            } catch (err) {
                handleVideoPlaybackError(video, err);
            }
        }

        const outterInnerBoxWidthRatio = 3.9/7.5;
        const heightWidthOutterRatio = 10.6/7.5;
        const heightWidthInnerRatio = 9/3.9;

        const renderLoop = () => {
            if (video.readyState >= 2) {
                if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                }
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                if(renderWireframe.current){
                    ctx.lineWidth = 5;
                    ctx.setLineDash([10, 3]);
                    ctx.strokeStyle  ='white';

                    let scale = 1.0;
                    let scancardBoxWidth = 0.8*canvas.width;
                    let scancardBoxHeight = heightWidthOutterRatio*scancardBoxWidth;

                    if(scancardBoxHeight > canvas.height){
                        scale = canvas.height/(scancardBoxHeight*1.1);
                    }

                    scancardBoxWidth *= scale;
                    scancardBoxHeight *= scale;

                    let { x, y } = {
                        x: (canvas.width - scancardBoxWidth)/2,
                        y: (canvas.height - scancardBoxHeight)/2
                    };

                    ctx.strokeRect(x, y, scancardBoxWidth, scancardBoxHeight);

                    // Set font properties
                    ctx.font = '35px Arial';     // font size and family
                    ctx.fillStyle = 'white';     // text color
                    ctx.textAlign = 'left';      // horizontal alignment: 'left', 'center', 'right'
                    ctx.textBaseline = 'top';    // vertical alignment: 'top', 'middle', 'bottom', 'alphabetic'

                    // Draw text at position (x, y)
                    ctx.fillText('ScanCard (optional)', x+10, y+10);

                    const keyBoxWidth = outterInnerBoxWidthRatio * scancardBoxWidth;
                    const keyBoxHeight =heightWidthInnerRatio*keyBoxWidth;
                    ({ x, y } = {
                        x: (canvas.width - keyBoxWidth)/2,
                        y: (canvas.height - keyBoxHeight)/2
                    });
                    ctx.strokeRect(x, y, keyBoxWidth, keyBoxHeight);

                    // Draw text at position (x, y)
                    ctx.fillText('Schlüssel (verpflichtend)', x+10, y+10);

                    ctx.setLineDash([]);
                }
            }
            if(keepRendering.current){
                requestAnimationFrame(renderLoop);
            }
        }
        requestAnimationFrame(renderLoop);
    }

    const handleVideoPlaybackError = (video, err) => {
        keepRendering.current = false;
        video.srcObject = null;
        closeResources();
        streamRef.current = null;
        setState(ImageCaptureState.ACCESS_DENIED);
        console.log("Camera video playback was blocked.", err);
    }

    const getVideo = () => {
        return document.getElementById("image-capture-helper-vid");
    }
    const getCanvas = () => {
        return document.getElementById('image-capture-helper-canvas');
    }

    const fallbackCanvasSnapshot = () => {
        // otherwise we got it on the picture
        renderWireframe.current = false;
        // Wait a little for autofocus/exposure to settle
        setTimeout(() => {
            keepRendering.current = false;
            //alert("captured with normal canvas")
            setState(ImageCaptureState.CAPTURED);
            setTimeout(() => renderWireframe.current = true, 200);
        }, 500); // 500ms delay
    }

    // taking picture handling
    const restartCameraStream = async() => {
        // Stop and clean up the old stream if any
        if (streamRef.current) {
            await stopStream(streamRef.current);
            streamRef.current = null;
        }

        // Initialize a new camera stream
        const stream = await initializeCameraStream();
        streamRef.current = stream;

        // Wait until the first video track is ready
        const track = stream.getVideoTracks()[0];
        await waitForTrackReady(track);


        return stream;
    }

    const stopStream = (stream) => {
        return new Promise((resolve) => {
            const tracks = stream.getTracks();
            if (tracks.length === 0) return resolve();

            tracks.forEach(t => t.stop());

            // Polling until all tracks report 'ended'
            const checkEnded = () => {
                if (tracks.every(t => t.readyState === 'ended')) {
                    resolve();
                } else {
                    requestAnimationFrame(checkEnded);
                }
            };
            checkEnded();
        });
    }

    // Waits for the track to be 'live' and ready
    const  waitForTrackReady = (track) => {
        return new Promise((resolve, reject) => {
            const rejectTimeout = setTimeout(() => reject(new Error('Track not ready in time')), 3000);
            if (track.readyState === 'live') return resolve();
            const onLive = () => {
                track.removeEventListener('unmute', onLive);
                clearTimeout(rejectTimeout);
                resolve();
            };
            track.addEventListener('unmute', onLive);
        });
    }

    const takePicture = async () => {
        if ('ImageCapture' in window) {
            try {
                keepRendering.current = false;
                //streamRef.current = await restartCameraStream();

                const track = streamRef.current.getVideoTracks()[0];

                const canvas = getCanvas();

                const imageCapture = new ImageCapture(track);
                // Optionally test takePhoto to detect support (or test photoCapabilities)
                await imageCapture.getPhotoCapabilities(); // may throw if unsupported

                console.log('ImageCapture.takePhoto() appears supported');

                const blob = await imageCapture.takePhoto();

                const imgBitmap = await createImageBitmap(blob);
                // draw to canvas (so we can overlay inside canvas too)
                canvas.width = imgBitmap.width;
                canvas.height = imgBitmap.height;
                const ctx = canvas.getContext('2d');
                setState(ImageCaptureState.CAPTURED);
                ctx.drawImage(imgBitmap, 0, 0, canvas.width, canvas.height);
                imgBitmap.close();
                //alert("captured with imagecapture.takephoto")
            } catch (err) {
                fallbackCanvasSnapshot();
                //alert(err);
                console.log('ImageCapture appears not fully supported:', err);
            }
        } else {
            fallbackCanvasSnapshot();
            console.log('ImageCapture not in window');
        }
    }

    const saveImage = () => {
        getCanvas().toBlob((img) => {
            const hasFirstFoto =  !!foto1;
            !hasFirstFoto ? setFoto1(img) : setFoto2(img);

            if(!hasFirstFoto){
                setState(ImageCaptureState.STREAMING);
            }else{
                closeResources();
                setState(ImageCaptureState.DONE);
            }
        }, "image/jpeg", 0.9);
    }

    const closeResources = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
        }
    }

    const done = () => {
        setter(foto1, foto2);
    }

    return (<div className="image-capture-helper" style={{overflow: showLandscapeMsg ? 'hidden' : 'visible' }}>
        {showLandscapeMsg && <div style={{position: "absolute", zIndex: 1000, width: '100dvw', height: '100dvh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: "var(--background)"}}>
            <h1>Bitte Telefon im Portraitmodus verwenden.</h1>
        </div>}
        <>
            {state == ImageCaptureState.EXPLAIN && <div style={{padding: '10px'}}>
                <h1>Aufnahme-Hilfe</h1>
                <p>Die Aufnahme-Hilfe bindet Ihre Kamera ein und zeigt einen Rahmen an. Falls Sie eine Scancard verwenden, bitte beide Rahmen in etwa passend positionieren.
                    Ansonsten den Schlüssel im inneren Rahmen so positionieren, dass dieser den Rahmen ausfüllt.</p>
                <p><strong>Hierzu wird Zugriff auf Ihre Kamera benötigt, bitte stimmen Sie der Abfrage zu. Falls Sie das
                    nicht möchten, können Sie auch ein Bild hochladen.</strong></p>
                <button type={"button"} onClick={initializeCameraStream}>FOTOS machen</button>
            </div>}

            {(state == ImageCaptureState.STREAMING || state == ImageCaptureState.CAPTURED) && <div style={{padding: '10px'}}>
                <div className={"image-capture-helper-video-wrapper"}>
                    <h1 className={"image-capture-helper-video-header"}>Foto {!foto1 && <>1</>}{!!foto1 && <>2</>}</h1>
                    <video id="image-capture-helper-vid" style={{display: 'none'}} playsInline muted></video>
                    <canvas id="image-capture-helper-canvas"></canvas>

                    <div className={"image-capture-helper-btn-row"}>
                        {state == ImageCaptureState.STREAMING &&
                            <button type={"button"} onClick={takePicture}>Foto aufnehmen</button>}
                        {state == ImageCaptureState.CAPTURED && <>
                            <button type={"button"} onClick={() => setState(ImageCaptureState.STREAMING)}>Nochmals</button>
                            <button type={"button"} onClick={saveImage}>
                                {!foto1 && <>Nächstes Seite</>}
                                {!!foto1 && <>Abschließen</>}
                            </button>
                        </>}
                    </div>
                </div>
            </div>}
            { state == ImageCaptureState.ACCESS_DENIED && <div style={{padding: '10px'}}>
                <h1>Zugriff wurde verweigert</h1>
                <p>Wir können leider nicht auf Ihre Kamera zugreifen, daher kann die Aufnahme-Hilfe nicht verwendet
                    werden.<br/>
                    Bitte nützen Sie die Möglichkeit des Dateiuploads im Prozess stattdessen.</p>
                <button type={"button"} onClick={close}>Zurück zum Prozess</button>
            </div>
            }
        </>
    </div>)
}
